import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_WEB } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  ContentControlApplicationRule,
  ContentControlDataProtectionExclusion,
  ContentControlDataProtectionRule,
  ContentControlWebAccessRule,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  ContentControlWebFormValue,
  ContentControlWebSearchableKeywords,
  ScheduleListItem,
} from '../../../../utils/uiSettings/contentControl/content-control.types';
import {
  CONTENT_CONTROL_WEB_ACCESS_DEFAULT_VALUE,
  getContentControlWebSearchableKeywords,
} from '../../../../utils/uiSettings/contentControl/content-control.values';
import { LabelWidthValues } from '../../../../../../../types/common/types';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzContentControlWebAccessExceptionsGridWebComponent } from '../gridComponents/webAccessRules/GzContentControlWebAccessExceptionsGridWebComponent';
import { GzContentControlDataProtectionRulesGridWebComponent } from '../gridComponents/dataProtectionRules/GzContentControlDataProtectionRulesGridWebComponent';
import { GzContentControlDataProtectionExclusionsGridWebComponent } from '../gridComponents/dataProtectionExclusions/GzContentControlDataProtectionExclusionsGridWebComponent';
import { GzContentControlApplicationRulesGridWebComponent } from '../gridComponents/applicationRules/GzContentControlApplicationRulesGridWebComponent';
import { DataLoadedStatus, PolicyAppState } from '../../../../redux/types';
import {
  getIsContentControlWebAdditionalInfoLoaded,
} from '../../../../redux/customStates/contentControl/content-control.actions';
import { GzHardCodedFormField } from '../../../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { selectPoliciesState } from '../../../../redux/slice';
import { store } from '@nirvana/redux/store';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_WEB)
export class GzContentControlWebWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  ContentControlWebFormValue,
  ContentControlWebSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.CONTENT_CONTROL,
    sectionId: SectionId.CONTENT_CONTROL_WEB,
  };
  protected searchableKeywords = getContentControlWebSearchableKeywords();

  @query('#web-access-enable-field')
  private webAccessEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#content-control-web-info-text')
  private webInfoText: HTMLElement;
  @query('#web-access-content')
  private webAccessContent: HTMLElement;
  @query('#web-categories-schedule-id-field')
  private scheduleIdField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#blocklist-rules-application-blacklisting-warning-text')
  private blocklistRulesWarningText: HTMLElement;

  @query('#web-categories-enable-exclusions')
  private enableExclusionsField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#web-categories-exclusions-field')
  private exclusionsItemsFormField: GzFormFieldWebComponent<
    GzContentControlWebAccessExceptionsGridWebComponent,
    ContentControlWebAccessRule[]
  >;

  @query('#application-enable')
  private applicationEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#application-rules-field')
  private applicationRulesField: GzFormFieldWebComponent<
    GzContentControlApplicationRulesGridWebComponent,
    ContentControlApplicationRule[]
  >;

  @query('#data-protection-enable')
  private dataProtectionEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#data-protection-rules-field')
  private dataProtectionRulesField: GzFormFieldWebComponent<
    GzContentControlDataProtectionRulesGridWebComponent,
    ContentControlDataProtectionRule[]
  >;
  @query('#data-protection-exclusions-field')
  private dataProtectionExclusionsField: GzFormFieldWebComponent<
    GzContentControlDataProtectionExclusionsGridWebComponent,
    ContentControlDataProtectionExclusion[]
  >;
  @query('#data-protection-exclusions-content')
  private dataProtectionExclusionsContent: HTMLElement;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="web-access-enable-field" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[ContentControlWebSearchableKeywords.WebAccessControl].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <gz-title class="second-title">
          ${this.searchableKeywords[ContentControlWebSearchableKeywords.WebAccessControl].translatedLabel}
        </gz-title>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="content-control-web-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'Web Access Control enables you to manage internet access for users or applications, ensuring productivity and security during designated time periods.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div>
        <gz-form-field id="web-categories-schedule-id-field" class="mt-2" status-field>
          <gz-dropdown
            canDelete
            showDeleteIcon
            gz-form-input
            type="dropdown"
            .labelWidth="${LabelWidthValues.XLARGE}"
            .label="${translateFn('Assign scheduler')}"
            .noResultsFound="${translateFn('No scheduler available.')}"
            .placeholder="${translateFn('No scheduler selected.')}"
          ></gz-dropdown>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <div id="web-access-content">
          <gz-form-field id="web-categories-enable-exclusions" class="mbw-1 mt-4">
            <gz-toggle
              gz-form-input
              @toggleChange="${e => toggleVisibility(this.exclusionsItemsFormField, e.detail.checked)}"
            >
              <gz-title fontSize="11" fontWeight="600" class="toggle-subtitle" searchable-label="${
                this.searchableKeywords[ContentControlWebSearchableKeywords.Exclusions].label
              }">${this.searchableKeywords[ContentControlWebSearchableKeywords.Exclusions].translatedLabel}</gz-title>
            </gz-toggle>
          </gz-form-field>

          <gz-text fontSize="12" fontWeight="400" class="infoText"
            >${translateFn('Configure exclusions to allow users access to specific webpages.')}
          </gz-text>

          <gz-form-field id="web-categories-exclusions-field">
            <gz-policy-panel-content-control-web-access-exceptions-grid
              class="removeGridPadding"
              gz-form-input
            ></gz-policy-panel-content-control-traffic-exclusions-grid>
          </gz-form-field>
        </div>
      </div>

      <div class="mt-4">
        <gz-form-field id="application-enable" status-field class="mbw-1">
          <gz-toggle
            gz-form-input
            @toggleChange="${e => {
              toggleVisibility(this.applicationRulesField, e.detail.checked);
              toggleVisibility(this.blocklistRulesWarningText, !e.detail.checked);
            }}"
            searchable-label="${
              this.searchableKeywords[ContentControlWebSearchableKeywords.ApplicationBlacklist].label
            }"
          >
            <gz-title class="second-title">${
              this.searchableKeywords[ContentControlWebSearchableKeywords.ApplicationBlacklist].translatedLabel
            }</gz-title>
          </gz-toggle>
        </gz-form-field>

        <gz-text fontSize="12" fontWeight="400" class="infoText"
        >${translateFn(
          "Application Blacklisting helps you completely block or restrict users' access to applications on their systems."
        )}
        </gz-text>

        <gz-info-box
          id="blocklist-rules-application-blacklisting-warning-text"
          class="padding-top-after-info-text hidden pl-0"
          severity="warning"
        >
          ${translateFn(
            'Disabling the Content Control module, regardless of the method, will also disable the application path blocklist rules in the Blocklist section.'
          )}
        </gz-info-box>

        <gz-form-field id="application-rules-field" status-field>
          <gz-policy-panel-content-control-application-rules-grid
            class="removeGridPadding"
            gz-form-input
          ></gz-policy-panel-content-control-application-rules-grid>
        </gz-form-field>
      </div>

      <div class="mt-4">
        <gz-form-field id="data-protection-enable" status-field class="mbw-1">
          <gz-toggle
            gz-form-input
            @toggleChange="${e => {
              toggleVisibility(this.dataProtectionRulesField, e.detail.checked);
              toggleVisibility(this.dataProtectionExclusionsContent, e.detail.checked);
            }}"
            searchable-label="${this.searchableKeywords[ContentControlWebSearchableKeywords.DataProtection].label}"
          >
            <gz-title class="second-title"
            >${this.searchableKeywords[ContentControlWebSearchableKeywords.DataProtection].translatedLabel}</gz-title
            >
          </gz-toggle>
        </gz-form-field>

        <gz-text fontSize="12" fontWeight="400" class="infoText"
        >${translateFn(
          'Data Protection prevents unauthorized disclosure of sensitive data based on administrator-defined rules.'
        )}
        </gz-text>

        <gz-form-field id="data-protection-rules-field" status-field>
          <gz-policy-panel-content-control-data-protection-rules-grid
            class="removeGridPadding"
            gz-form-input
          ></gz-policy-panel-content-control-data-protection-rules-grid>
        </gz-form-field>
      </div>

      <div id="data-protection-exclusions-content">
        <gz-title fontSize="12" fontWeight="600" class="toggle-subtitle mt-4" searchable-label="${
          this.searchableKeywords[ContentControlWebSearchableKeywords.Exclusions].label
        }">${this.searchableKeywords[ContentControlWebSearchableKeywords.Exclusions].translatedLabel}</gz-title>

        <gz-text fontSize="12" fontWeight="400" class="infoText mt-1"
        >${translateFn(
          'Configure exclusions to data protection rules, allowing users to continue sending protected data to authorized websites and recipients.'
        )}
        </gz-text>
        <gz-text fontSize="12" fontWeight="400" class="infoText mt-1"
        >${translateFn('Exclusions can be applied to individual rules or to all rules at once. ')}
        </gz-text>

        <gz-form-field id="data-protection-exclusions-field">
          <gz-policy-panel-content-control-data-protection-exclusions-grid
            class="removeGridPadding"
            gz-form-input
          ></gz-policy-panel-content-control-data-protection-exclusions-grid>
        </gz-form-field>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.contentControl
   */
  protected getValueFromManager(): ContentControlWebFormValue {
    // get default value
    const defaultValue = CONTENT_CONTROL_WEB_ACCESS_DEFAULT_VALUE;
    const policyManagerInstance = PolicyPanelManager.getInstance();

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.CONTENT_CONTROL]: {
          webAccess: policyManagerInstance.getPolicy('uiSettings.contentControl.webAccess'),
          webCategoriesFilter: policyManagerInstance.getPolicy('uiSettings.contentControl.webCategoriesFilter'),
          dataProtection: policyManagerInstance.getPolicy('uiSettings.contentControl.dataProtection'),
          application: policyManagerInstance.getPolicy('uiSettings.contentControl.application'),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        contentControl: {
          webAccess: {
            enable: this.webAccessEnableField,
            profileType: new GzHardCodedFormField(0),
            useExceptions: this.enableExclusionsField,
            webRules: this.exclusionsItemsFormField,
            scheduler: new GzHardCodedFormField([]),
          },
          webCategoriesFilter: {
            profileType: new GzHardCodedFormField(0),
            scheduleId: this.scheduleIdField,
          },
          dataProtection: {
            enable: this.dataProtectionEnableField,
            rules: this.dataProtectionRulesField,
            exclusions: this.dataProtectionExclusionsField,
          },
          application: {
            enable: this.applicationEnableField,
            rules: this.applicationRulesField,
          },
        },
      },
    });

    this.dataProtectionExclusionsField.Input.fieldWithRulesToConsider = this.dataProtectionRulesField;
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // get response status
    const policiesApp = selectPoliciesState(store.getState());
    const responseStatus = getIsContentControlWebAdditionalInfoLoaded(policiesApp);

    // set scheduler options
    if (responseStatus === DataLoadedStatus.SUCCESS) {
      const additionalSchedulers = PolicyPanelManager.getInstance().getAdditionalPolicyInfo(
        ['contentControl', 'scheduleList'],
        []
      ) as ScheduleListItem[];
      this.scheduleIdField.Input.options = additionalSchedulers.map(scheduler => {
        return {
          value: scheduler._id,
          title: scheduler.name,
        };
      });
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        contentControl: {
          webAccess: this.valueT.uiSettings.contentControl.webAccess,
          webCategoriesFilter: this.valueT.uiSettings.contentControl.webCategoriesFilter,
          dataProtection: this.valueT.uiSettings.contentControl.dataProtection,
          application: this.valueT.uiSettings.contentControl.application,
        },
      },
    };

    // unset current value, because we don't have scheduler options
    if (responseStatus !== DataLoadedStatus.SUCCESS) {
      this.scheduleIdField.value = null;
    }

    // show delete icon if scheduleId is set
    const hasScheduleId =
      responseStatus === DataLoadedStatus.SUCCESS &&
      this.valueT.uiSettings.contentControl.webCategoriesFilter.scheduleId !== null;
    this.scheduleIdField.Input.setShowDeleteIcon(hasScheduleId);

    this.exclusionsItemsFormField.Input.setPolicyId(PolicyPanelManager.getInstance().getPolicyId());
    this.exclusionsItemsFormField.Input.setExportButtonDisabledState();
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // convert scheduleId to null if it's empty
    const scheduleId = _.get(value, ['uiSettings', 'contentControl', 'webCategoriesFilter', 'scheduleId'], '');
    if (scheduleId === '') {
      _.set(value, ['uiSettings', 'contentControl', 'webCategoriesFilter', 'scheduleId'], null);
    }

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'contentControl'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.scheduleIdField, !event.detail.checked);
    toggleDisabled(this.webInfoText, !event.detail.checked);
    toggleVisibility(this.webAccessContent, event.detail.checked);
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();
    this.exclusionsItemsFormField.Input.reset();
    this.applicationRulesField.Input.reset();
    this.dataProtectionRulesField.Input.reset();
    this.dataProtectionExclusionsField.Input.reset();
  }

  /**
   * Override method to check if additional info is loaded.
   * @param policiesApp
   */
  protected isAdditionalInfoLoaded(policiesApp: PolicyAppState): boolean {
    return getIsContentControlWebAdditionalInfoLoaded(policiesApp) !== DataLoadedStatus.NOT_LOADED;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_WEB: GzContentControlWebWebComponent;
  }
}
